// Notifications bell hook. Polls the aggregator endpoint and
// surfaces pending urgent operator actions (Google token
// revoked per agent, skills awaiting review, etc.).

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";

export type NotificationSeverity = "urgent" | "warning" | "info";

/**
 * Frontend mirror of the backend NotificationAction union.
 * The row component switches on `type` to decide whether to
 * router.push, fire an inline Google OAuth flow, or render no
 * CTA at all.
 */
export type NotificationAction =
  | { type: "navigate"; href: string }
  | {
      type: "google_reconnect";
      customerId: string;
      template: string;
      displayName: string;
    }
  | { type: "none" };

export interface NotificationItem {
  id: string;
  kind: string;
  severity: NotificationSeverity;
  title: string;
  body: string;
  action: NotificationAction;
  ctaLabel: string | null;
  at: string;
}

export interface NotificationsResponse {
  count: number;
  items: NotificationItem[];
}

export function useNotifications(customerId: string | null) {
  return useQuery({
    queryKey: ["notifications", customerId],
    enabled: !!customerId,
    queryFn: ({ signal }) =>
      apiFetch<NotificationsResponse>(
        `/api/customers/${customerId}/notifications`,
        { signal },
      ),
    // 60s is enough for a bell — any shorter burns Google API
    // budget via the backend's per-agent token check.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}
